import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { useTranslation } from "react-i18next";
import React, { useMemo, useCallback } from "react";

type Role = {
  id: string;
  name: string;
  description?: string;
  is_system?: boolean;
  permissions?: Array<{ permission: string; allowed: boolean }>;
};

type RolesTabProps = {
  orgId: string | null;
  roles: Role[];
  canManageRoles: boolean;
  onOpenEdit: (role: Role) => void;
  selectedRoleIds: string[];
  setSelectedRoleIds: React.Dispatch<React.SetStateAction<string[]>>;
};

export function RolesTab({
  orgId,
  roles,
  canManageRoles,
  onOpenEdit,
  selectedRoleIds,
  setSelectedRoleIds,
}: RolesTabProps) {
  const { t, i18n } = useTranslation(['pages.settings', 'common']);

  // Get translated display name for a role from API-provided translations
  const getRoleDisplayName = useCallback((role: Role): string => {
    const translations = (role as any).translations as Record<string, string> | undefined;
    const languageCode = i18n.language.split('-')[0]; // e.g., 'en-US' -> 'en'
    
    // Use API-provided translations if available
    if (translations && translations[languageCode]) {
      return translations[languageCode];
    }
    
    // Preserve custom role name casing exactly as stored.
    return role.name;
  }, [i18n.language]);

  // Only owner role is protected from selection/deletion
  // Other system roles (admin, user, viewer) can be selected and deleted
  const selectableRoles = useMemo(
    () => roles.filter((role) => canManageRoles && role.name !== 'owner'),
    [roles, canManageRoles]
  );
  const allSelected = selectableRoles.length > 0 && selectableRoles.every((role) => selectedRoleIds.includes(role.id));
  const someSelected = selectedRoleIds.length > 0 && !allSelected;

  return (
    <div>
        {!orgId ? (
          <div className="text-xs text-slate-600">{t('pages.settings:sections.roles.noOrganization')}</div>
        ) : (
          <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-xs text-slate-600">
              {t('pages.settings:sections.roles.description')}
            </div>
            <div />
          </div>
          <div className="bg-white shadow-none rounded-none overflow-hidden">
            <div className="w-full overflow-hidden">
              <div className="overflow-x-auto overflow-y-auto">
              <table className="min-w-full listen-table listen-table-black">
                <thead className="sticky top-0 z-10">
                  <tr className="table-header-row">
                    <th className="table-header-cell w-10">
                      <Checkbox
                        checked={allSelected ? true : someSelected ? "indeterminate" : false}
                        onCheckedChange={() => {
                          if (allSelected) {
                            setSelectedRoleIds([]);
                            return;
                          }
                          setSelectedRoleIds(selectableRoles.map((role) => role.id));
                        }}
                        aria-label={t('table.headers.selectAll')}
                      />
                    </th>
                    <th className="table-header-cell w-48">
                      <span>{t('pages.settings:sections.roles.table.position')}</span>
                    </th>
                    <th className="table-header-cell flex-1">
                      <span>{t('pages.settings:sections.roles.table.description')}</span>
                    </th>
                    <th className="table-header-cell w-32">
                      <span>{t('pages.settings:sections.roles.table.type')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {roles?.length ? roles.map((role: Role) => (
                    <tr key={role.id} className="table-body-row">
                      <td className="table-body-cell">
                        <Checkbox
                          checked={selectedRoleIds.includes(role.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedRoleIds((prev) => [...prev, role.id]);
                            } else {
                              setSelectedRoleIds((prev) => prev.filter((id) => id !== role.id));
                            }
                          }}
                          disabled={!canManageRoles || role.name === 'owner'}
                          aria-label={t('table.headers.selectRow')}
                        />
                      </td>
                      <td className="table-body-cell">
                        <button
                          onClick={() => onOpenEdit(role)}
                          disabled={!canManageRoles}
                          className={`text-left whitespace-nowrap overflow-hidden text-ellipsis ${
                            canManageRoles
                              ? 'text-black hover:underline'
                              : 'text-slate-600 cursor-not-allowed'
                          }`}
                        >
                          {getRoleDisplayName(role)}
                        </button>
                      </td>
                      <td className="table-body-cell">{role.description || t('pages.settings:sections.roles.table.noDescription')}</td>
                      <td className="table-body-cell">
                        {role.is_system ? t('pages.settings:sections.roles.table.typeSystem') : t('pages.settings:sections.roles.table.typeCustom')}
                      </td>
                    </tr>
                  )) : (
                    <tr className="table-body-row">
                      <td className="table-body-cell text-center" colSpan={4}>
                        {t('pages.settings:sections.roles.table.empty')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
            </div>
          </div>
        </div>
        )}
    </div>
  );
}

export default RolesTab;
